import React from "react";

import { Alert, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormInput } from "@components/index";
import { SelectCell } from "@components/SelectCell/SelectCell";
import TableTemplate from "@components/Table/TableTemplate";
import { PREREGISTRATION_BOOKS_ENDPOINT } from "@constants/Endpoints";
import { Roles } from "@constants/Roles";
import {
    getReturnBookPurchase,
    getReturnBookStatus,
    returnBookPurchase,
    returnBookStatus,
} from "@constants/SchoolBookReturns";
import { bookNumberMask } from "@helpers/utils/utils";
import { SelectSchoolModal } from "@pages/Logged/Common/SchoolStats/SelectSchoolModal/SelectSchoolModal";

import { SaveChangesModal } from "./SaveChangesModal/SaveChangesModal";
import { SchoolReturnCompleteModal } from "./SchoolReturnCompleteModal/SchoolReturnCompleteModal";
import "./SchoolReturnPage.scss";

/**
 * SchoolReturnPageView component renders the school return page view.
 * It displays a table of books with various columns and allows users to interact with the data,
 * such as changing the status or invoice of a book, selecting a school, and completing or editing the book status.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.schoolList - List of schools available for selection.
 * @param {SchoolPreRegistrationDataResource} props.school - The currently selected school.
 * @param {Object} props.userData - User data containing permissions and actions.
 * @param {boolean} props.userData.hasActionsEnabled - Indicates if the user has actions enabled.
 * @param {number} props.userData.role - The role of the user.
 * @param {Array} props.changesList - List of changes made to the books.
 * @param {boolean} props.showCompleteModal - Indicates if the complete modal is visible.
 * @param {boolean} props.showSelectModal - Indicates if the select school modal is visible.
 * @param {boolean} props.showSaveChangesModal - Indicates if the save changes modal is visible.
 * @param {boolean} props.firstTime - Indicates if this is the first time the user is accessing the page.
 * @param {React.MutableRefObject} props.tableTemplateRef - Reference to the table template component.
 * @param {React.MouseEventHandler<HTMLDivElement>} props.onSelectSchoolClick - Callback for when the select school button is clicked.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onBookStatusEditButtonClick - Callback for when the edit book status button is clicked.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onBookStatusButtonClick - Callback for when the complete book status button is clicked.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onSaveChangesTableButtonClick - Callback for when the save changes button in the table is clicked.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onSchoolVerifyButtonClick - Callback for when the verify button is clicked.
 * @param {(book: BookSchoolPreRegResource, comment: string) => void} props.onEmployeePreRegistrationCommentInputChange - Callback for when the employee pre-registration comment input changes.
 * @param {Function} props.onCompleteModalHide - Callback for hiding the complete modal.
 * @param {Function} props.onCompleteModalComplete - Callback for completing the school book status.
 * @param {Function} props.onSelectSchool - Callback for selecting a school in the modal.
 * @param {Function} props.onSelectModalHide - Callback for hiding the select school modal.
 * @param {Function} props.onSaveChangesModalSubmit - Callback for submitting changes in the save changes modal.
 * @param {Function} props.onSaveChangesModalHide - Callback for hiding the save changes modal.
 * @param {(book: BookSchoolPreRegResource) => void} props.onStatusSelect - Callback for selecting a status in the table.
 * @param {(book: BookSchoolPreRegResource) => void} props.onPurchaseSelect - Callback for selecting an invoice in the table.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onProfileButtonClick - Callback for navigating to the profile page.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onSchoolsExportButtonClick - Callback for exporting the list of schools.
 * @returns {JSX.Element} The rendered SchoolReturnPageView component.
 */
function SchoolReturnPageView(props) {
    const [t] = useTranslation();
    const isIddinkEmployee =
        props.userData?.role === Roles.IddinkSchoolManager ||
        props.userData?.role === Roles.IddinkEmployee ||
        props.userData?.role === Roles.IddinkUserManager;

    const commentInputEnabled =
        isIddinkEmployee && props.school?.bookStatusFinished && !props.school?.adminBookStatusFinished;
    const bookDeliveryEnabled =
        props.school?.schoolActiveNextYear &&
        ((!isIddinkEmployee && !props.school?.bookStatusFinished) ||
            (isIddinkEmployee && props.school?.bookStatusFinished));
    const bookTakeoverEnabled =
        !props.school?.schoolActiveNextYear &&
        ((!isIddinkEmployee && !props.school?.bookStatusFinished) ||
            (isIddinkEmployee && props.school?.bookStatusFinished));
    const isSchoolFinished = props.school?.bookStatusFinished;
    const showWarningAlert =
        !isIddinkEmployee && props.school?.bookStatusFinished && !props.school?.adminBookStatusFinished;
    const showVerifiedAlert = props.school?.bookStatusFinished && props.school?.adminBookStatusFinished;

    const columns = [
        {
            name: "bookNumber",
            filterName: "bookNumber",
            sortName: "bookNumber",
            headerCaption: t("BookNumber"),
            caption: (x) => bookNumberMask(x.bookNumber),
            filterPattern: /^(\d+\.?)+$/,
            filterParser: (x) => x.replace(/\./g, ""),
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "title",
            filterName: "bookTitle",
            sortName: "bookTitle",
            headerCaption: t("Title"),
            size: "big",
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "isbn",
            filterName: "isbn",
            sortName: "isbn",
            headerCaption: t("ISBN"),
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "booksQuantity",
            filterName: "booksQuantity",
            sortName: "booksQuantity",
            headerCaption: t("booksQuantity"),
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        isSchoolFinished && {
            name: "employeePreRegistrationComment",
            headerCaption: t("Comment"),
            size: "medium",
            canSort: false,
            canFilter: false,
            canEdit: false,
            caption: (/** @type {BookSchoolPreRegResource} */ book) =>
                commentInputEnabled ? (
                    // @ts-ignore
                    <FormInput
                        value={
                            props.changesList.find((x) => x.id === book.id)?.employeePreRegistrationComment ||
                            book.employeePreRegistrationComment
                        }
                        change={(comment) => props.onEmployeePreRegistrationCommentInputChange(book, comment)}
                    />
                ) : (
                    <>{book.employeePreRegistrationComment}</>
                ),
        },
        {
            name: "shouldBeReturned",
            headerCaption: t("bookDelivery"),
            size: !props.school?.schoolActiveNextYear ? "small" : "medium",
            canSort: false,
            canFilter: false,
            canEdit: false,
            caption: (/** @type {BookSchoolPreRegResource} */ book) =>
                bookDeliveryEnabled ? (
                    <SelectCell
                        options={returnBookStatus}
                        selected={getReturnBookStatus(book.shouldBeReturned)}
                        setSelected={() => props.onStatusSelect(book)}
                        disabled={!book.canEdit}
                    />
                ) : (
                    <>{getReturnBookStatus(book.shouldBeReturned).name}</>
                ),
        },
        !props.school?.schoolActiveNextYear && {
            name: "shouldTakeOver",
            headerCaption: t("shouldTakeOver"),
            size: "medium",
            canSort: false,
            canFilter: false,
            canEdit: false,
            caption: (/** @type {BookSchoolPreRegResource} */ book) =>
                bookTakeoverEnabled ? (
                    <SelectCell
                        options={returnBookPurchase}
                        selected={getReturnBookPurchase(!book.shouldTakeOver)}
                        setSelected={() => props.onPurchaseSelect(book)}
                        disabled={!book.canEdit}
                    />
                ) : (
                    <>{getReturnBookPurchase(book.shouldTakeOver)?.name}</>
                ),
        },
    ];

    return (
        <div className="school-return-page">
            {props.schoolList.length > 0 ? (
                <>
                    <div className="header-page">
                        <div className="header-page-buttons-wrapper">
                            <div className="select-school-wrapper" onClick={props.onSelectSchoolClick}>
                                <div className="select-school">
                                    {props.school !== null && (
                                        <>
                                            <div className="school-number">{props.school.schoolNumber}</div>
                                            <div className="school-name">{props.school.name}</div>
                                        </>
                                    )}
                                </div>
                                <div className="icon">compare_arrows</div>
                            </div>
                            <div className="school-type">{props.school?.schoolDeliveryType}</div>
                            {!props.school?.schoolActiveNextYear && <div className="school-exit">Exit</div>}
                        </div>
                        {props.school !== null && (
                            <div className="school-book-status-wrapper">
                                {props.school.bookStatusFinished ||
                                    (props.school.adminBookStatusFinished && (
                                        <div className="message">{t("SchoolBookStatusIsFinished")}</div>
                                    ))}
                                {!isIddinkEmployee &&
                                    props.school.adminBookStatusFinished === false &&
                                    props.userData.hasActionsEnabled &&
                                    (props.school.bookStatusFinished ? (
                                        <Button
                                            className="button button-book-status-edit"
                                            onClick={props.onBookStatusEditButtonClick}
                                        >
                                            {t("Edit")}
                                        </Button>
                                    ) : (
                                        <Button
                                            className="button button-book-status"
                                            onClick={props.onBookStatusButtonClick}
                                        >
                                            {t("Complete")}
                                        </Button>
                                    ))}
                                {isIddinkEmployee && (
                                    <>
                                        <Button
                                            className="button button-book-export"
                                            onClick={props.onSchoolsExportButtonClick}
                                        >
                                            {t("ExportVerifiedCSV")}
                                        </Button>
                                        <hr className="export-button-separator" />
                                    </>
                                )}
                                {isIddinkEmployee && (
                                    <Button
                                        className="button button-book-verify"
                                        disabled={
                                            props.school.adminBookStatusFinished || !props.school?.bookStatusFinished
                                        }
                                        onClick={props.onSchoolVerifyButtonClick}
                                    >
                                        {t("Verify")}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                    {showWarningAlert && (
                        <Alert className="alert" variant="warning">
                            <Alert.Heading>Wait for Iddink's verification</Alert.Heading>
                            <p>Placeholder text</p>
                        </Alert>
                    )}
                    {showVerifiedAlert && (
                        <Alert className="info" variant="success">
                            <Alert.Heading>The Pre-Registration is finished!</Alert.Heading>
                            <p>Placeholder text</p>
                        </Alert>
                    )}
                    <div className="content trans-fromTop">
                        {props.school !== null && (
                            <TableTemplate
                                ref={props.tableTemplateRef}
                                // @ts-ignore
                                title={t("Returns")}
                                endpoint={PREREGISTRATION_BOOKS_ENDPOINT + "?schoolId=" + props.school.id}
                                columns={columns}
                                uniqueId="id"
                                paginator={true}
                                isMultiSelect={false}
                                pageSize={100}
                                customButtons={[
                                    props.changesList.length > 0 && {
                                        name: t("SaveChanges"),
                                        action: props.onSaveChangesTableButtonClick,
                                    },
                                ]}
                            />
                        )}
                    </div>
                    {props.showCompleteModal && (
                        <SchoolReturnCompleteModal
                            show={props.showCompleteModal}
                            onHide={props.onCompleteModalHide}
                            complete={props.onCompleteModalComplete}
                        />
                    )}
                    {props.showSelectModal && (
                        <SelectSchoolModal
                            show={props.showSelectModal}
                            schoolList={props.schoolList}
                            schoolSelected={props.school}
                            setSchoolSelected={props.onSelectSchool}
                            onHide={props.onSelectModalHide}
                        />
                    )}
                    {props.showSaveChangesModal && (
                        <SaveChangesModal
                            school={props.school}
                            show={props.showSaveChangesModal}
                            changesList={props.changesList}
                            submit={props.onSaveChangesModalSubmit}
                            onHide={props.onSaveChangesModalHide}
                        />
                    )}
                </>
            ) : (
                props.firstTime === false && (
                    <div className="no-schools-asigned-wrapper">
                        <div className="title">{t("NoSchoolsAsigned")}</div>
                        <div className="text">{t("NoSchoolsAsignedText")}</div>
                        <Button className="button button-profile" onClick={props.onProfileButtonClick}>
                            {t("Profile")}
                        </Button>
                    </div>
                )
            )}
        </div>
    );
}

export default SchoolReturnPageView;
